import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  // Home,
  Layers,
  Users,
  UsersRound,
  Settings,
  Lock,
  Building2,
  GraduationCap,
  LogOut,
  // Puzzle,
  FolderOpen,
  CircleUser,
  Clipboard,
  Tag,
  ShieldCheck,
  List,
  Receipt,
  FileCheck,
  TrendingUp,
  Trophy,
  Gamepad2,
  Award,
  Zap,
  ClipboardList,
  ChevronDown,
  CreditCard,
  Map,
  ArrowLeftRight,
  Package,
  CalendarDays,
  MessageSquare,
  Upload,
} from 'lucide-react';
import { APP_VERSION } from '../../application/constants/version';
import { Auth } from 'aws-amplify';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { resetUsersStateAction } from '../../application/store/reducers/users';
import { resetRewardsStateAction } from '../../application/store/reducers/rewards';
import { resetDealsStateAction } from '../../application/store/reducers/deals';
import { useCurrentUserStore } from '../../application/store/zustand/useCurrentUserStore';
import useLeaderboardStore from '../../application/store/zustand/useLeaderboardStore';
import useOrgStore from '../../application/store/zustand/useOrgStore';
import { apiSlice } from '../../application/store/api/GraphQlQuery';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from './ui/sidebar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { UserAvatar } from './ui/avatar';
import { cn } from '../lib/utils';
import { isStarterAccount } from '../../application/common/utils/isStarterAccount';
import { usePermissions } from '../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../application/context/PermissionsContext/permissionTypes';
import featureFlag from '../../application/common/featureFlag';
import PaivIcon from '../assets/paiv-icon.svg';

declare const global: {
  me: {
    id: string;
    name: string;
    imageName?: string;
    role?: string;
    title?: string;
    isAdmin?: boolean;
    functionalRoles?: string[];
  };
  appSettings?: {
    features?: {
      games?: {
        enabled?: boolean;
      };
    };
  };
};

interface NavItem {
  title: string;
  url: string;
  icon: React.ElementType;
  isPro?: boolean;
  starterOnly?: boolean;
  proOnly?: boolean;
}

const mainNavItems: NavItem[] = [
  // { title: 'Home', url: '/v2/home', icon: Home },
  { title: 'Pipeline', url: '/v2/pipeline', icon: Layers },
  { title: 'People', url: '/v2/people', icon: Users },
  { title: 'Teams', url: '/v2/teams', icon: UsersRound, proOnly: true },
  {
    title: 'Training',
    url: '/v2/training',
    icon: GraduationCap,
    proOnly: true,
  },
  { title: 'Payments', url: '/v2/payments', icon: CreditCard, proOnly: true },
  { title: 'Feed', url: '/v2/feed', icon: MessageSquare, proOnly: true },
  { title: 'Map', url: '/v2/map', icon: Map, proOnly: true },
  {
    title: 'Scheduling',
    url: '/v2/scheduling',
    icon: CalendarDays,
    proOnly: true,
  },
  { title: 'Products', url: '/v2/products', icon: Package, proOnly: true },
  // { title: 'Integration Marketplace', url: '/v2/projects', icon: Puzzle },
];

const settingsNavItems: NavItem[] = [
  {
    title: 'General Information',
    url: '/v2/settings/general',
    icon: FolderOpen,
  },
  {
    title: 'Customer-Facing Profile',
    url: '/v2/settings/profile',
    icon: CircleUser,
  },
  {
    title: 'Company Information',
    url: '/v2/settings/company',
    icon: Building2,
  },
  {
    title: 'AI Settings',
    url: '/v2/settings/ai',
    icon: Zap,
    proOnly: true,
  },
  {
    title: 'Damage Reports',
    url: '/v2/settings/damage-reports',
    icon: Clipboard,
    starterOnly: true,
  },
  {
    title: 'Sales Tools',
    url: '/v2/settings/sales-tools',
    icon: Tag,
    isPro: true,
  },
  {
    title: 'Dashboard',
    url: '/v2/settings/dashboard',
    icon: ShieldCheck,
    isPro: true,
  },
  {
    title: 'DocuSeal Visibility',
    url: '/v2/settings/docuseal',
    icon: FileCheck,
    proOnly: true,
  },
  {
    title: 'Task List Management',
    url: '/v2/settings/task-list',
    icon: List,
  },
  {
    title: 'Billing',
    url: '/v2/settings/billing',
    icon: Receipt,
  },
  {
    title: 'Lead Imports',
    url: '/v2/people/import',
    icon: Upload,
  },
];

const ProBadge = () => (
  <span className="ml-auto flex items-center gap-1 rounded bg-sidebar-accent px-1.5 py-0.5 text-[10px] font-medium text-sidebar-foreground">
    <Lock className="size-2.5" />
    PRO
  </span>
);

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { setOpenMobile } = useSidebar();
  const isStarter = isStarterAccount();
  const { checkPermission, isSuperAdmin } = usePermissions();
  const [performanceOpen, setPerformanceOpen] = React.useState(
    location.pathname.startsWith('/v2/performance')
  );
  const [settingsOpen, setSettingsOpen] = React.useState(
    location.pathname.startsWith('/v2/settings')
  );
  const [isPaivSuperAdmin, setIsPaivSuperAdmin] = React.useState(false);
  const [openChangelog, setOpenChangelog] = React.useState(false);
  const orgData = useOrgStore((state) => state.orgData);

  React.useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const groups =
          user.signInUserSession?.accessToken?.payload?.['cognito:groups'] ||
          [];
        setIsPaivSuperAdmin(groups.includes('paiv_admin_rw'));
      } catch {
        setIsPaivSuperAdmin(false);
      }
    };
    checkSuperAdmin();
  }, []);

  const { resetStore: resetCurrentUserStore } = useCurrentUserStore();
  const { resetStore: resetLeaderboardStore } = useLeaderboardStore();
  const { resetStore: resetOrgStore } = useOrgStore();

  const isActive = (url: string) => location.pathname.startsWith(url);

  // Close mobile sidebar on navigation
  React.useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  const viewGamesPermission = checkPermission(
    PermissionKeys.GamesRewards_ViewGame
  );
  const isGamesEnabled =
    (global as any)?.appSettings?.features?.games?.enabled ?? true;

  const performanceSubItems = React.useMemo(() => {
    const items: { title: string; url: string; icon: React.ElementType }[] = [
      {
        title: 'Leaderboard',
        url: '/v2/performance/leaderboard',
        icon: Trophy,
      },
    ];

    if (isGamesEnabled && viewGamesPermission !== 'none') {
      items.push({
        title: 'Games',
        url: '/v2/performance/games',
        icon: Gamepad2,
      });
    }

    items.push({
      title: 'Rewards',
      url: '/v2/performance/rewards',
      icon: Award,
    });

    if (
      (global as any).me?.isAdmin ||
      (global as any).me?.functionalRoles?.includes('manager')
    ) {
      items.push({
        title: 'Activity',
        url: '/v2/performance/activity',
        icon: Zap,
      });
    }

    if ((global as any).me?.isAdmin) {
      items.push({
        title: 'Report',
        url: '/v2/performance/reports',
        icon: ClipboardList,
      });
    }

    return items;
  }, [isGamesEnabled, viewGamesPermission]);

  const viewPayments = checkPermission(PermissionKeys.Sales_ViewPayments);

  const filteredMainItems = React.useMemo(() => {
    return mainNavItems.filter((item) => {
      // First check starter/pro gating
      if (item.starterOnly && !isStarter) return false;
      if (item.proOnly && isStarter) return false;

      // Then check feature-specific flags
      if (item.title === 'Payments') {
        return featureFlag('payments') && viewPayments;
      }
      if (item.title === 'Map') {
        return featureFlag('webMap');
      }
      if (item.title === 'Scheduling') {
        return !featureFlag('disableScheduling');
      }
      if (item.title === 'Feed') {
        return !featureFlag('hideFeed');
      }
      if (item.title === 'Products') {
        return !featureFlag('hideProducts');
      }
      return true;
    });
  }, [isStarter, viewPayments]);

  const filteredSettingsItems = React.useMemo(() => {
    return settingsNavItems.filter((item) => {
      if (item.starterOnly) return isStarter;
      if (item.proOnly) return !isStarter;
      // Lead Imports mirrors V1's Settings entry: super admins only, gated by
      // the bulk-imports feature flag.
      if (item.title === 'Lead Imports') {
        return isSuperAdmin() && featureFlag('bulk-imports');
      }
      return true;
    });
  }, [isStarter, isSuperAdmin]);

  const handleLogout = React.useCallback(() => {
    // Reset Zustand stores
    resetLeaderboardStore();
    resetOrgStore();
    resetCurrentUserStore();

    // Reset Redux state
    dispatch(resetUsersStateAction());
    dispatch(resetRewardsStateAction());
    dispatch(resetDealsStateAction());
    dispatch(apiSlice.util.resetApiState());

    // Clear global cached state
    const g = global as any;
    g.me = undefined;
    g.appSettings = undefined;
    g.trainingVideos = undefined;
    g.notifications = 0;
    g.notificationCount = 0;
    g.refreshOnNotificationHandler = [];
    g.updateNotificationHandler = [];

    // Clear all react-query cache
    queryClient.clear();

    // Sign out and navigate to root
    Auth.signOut();
    navigate('/');
  }, [
    dispatch,
    navigate,
    queryClient,
    resetCurrentUserStore,
    resetLeaderboardStore,
    resetOrgStore,
  ]);

  const renderLogo = () => (
    <div className="flex items-center gap-2.5 px-2 py-3">
      <img src={PaivIcon} alt="PAIV" className="size-6" />
      <span className="text-xl font-semibold tracking-tight text-sidebar-foreground group-data-[collapsible=icon]:hidden">
        paiv
      </span>
    </div>
  );

  const renderUserProfile = () => (
    <div className="flex items-center gap-3 rounded-lg bg-sidebar-accent/50 p-2 mx-2 mb-2 group-data-[collapsible=icon]:mx-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:bg-transparent">
      <UserAvatar
        imageName={global.me?.imageName}
        name={global.me?.name}
        size="md"
        className="bg-sidebar-accent"
      />
      <div className="flex flex-col group-data-[collapsible=icon]:hidden">
        <span className="text-sm font-medium text-sidebar-foreground">
          {global.me?.name}
        </span>
        <span className="text-xs text-sidebar-foreground/60">
          {global.me?.title || global.me?.role}
        </span>
      </div>
    </div>
  );

  const renderNavItem = (item: NavItem) => (
    <SidebarMenuItem key={item.title}>
      <SidebarMenuButton
        asChild
        isActive={isActive(item.url)}
        tooltip={item.title}
      >
        <Link to={item.url}>
          <item.icon
            className={cn('size-4', isActive(item.url) && 'text-primary')}
          />
          <span className="truncate">{item.title}</span>
          {item.isPro && <ProBadge />}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  const renderMainNav = () => (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>{filteredMainItems.map(renderNavItem)}</SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  const renderPerformanceNav = () => (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setPerformanceOpen(!performanceOpen)}
              isActive={location.pathname.startsWith('/v2/performance')}
              tooltip="Performance"
            >
              <TrendingUp
                className={cn(
                  'size-4',
                  location.pathname.startsWith('/v2/performance') &&
                  'text-primary'
                )}
              />
              <span className="truncate">Performance</span>
              <ChevronDown
                className={cn(
                  'ml-auto size-4 transition-transform duration-200',
                  !performanceOpen && '-rotate-90'
                )}
              />
            </SidebarMenuButton>
            {performanceOpen && (
              <SidebarMenuSub>
                {performanceSubItems.map((item) => (
                  <SidebarMenuSubItem key={item.title}>
                    <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                      <Link to={item.url}>
                        <item.icon
                          className={cn(
                            'size-4',
                            isActive(item.url) && 'text-primary'
                          )}
                        />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  const renderSettingsNav = () => (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setSettingsOpen(!settingsOpen)}
              isActive={location.pathname.startsWith('/v2/settings')}
              tooltip="Settings"
              className="text-sidebar-foreground/50"
            >
              <Settings
                className={cn(
                  'size-4',
                  location.pathname.startsWith('/v2/settings')
                    ? 'text-primary'
                    : 'text-sidebar-foreground/50'
                )}
              />
              <span className="truncate">Settings</span>
              <ChevronDown
                className={cn(
                  'ml-auto size-4 transition-transform duration-200',
                  !settingsOpen && '-rotate-90'
                )}
              />
            </SidebarMenuButton>
            {settingsOpen && (
              <div className="mt-1 py-1">
                {filteredSettingsItems.map((item) => {
                  const active = isActive(item.url);
                  const isDisabled = item.isPro && isStarter;
                  return (
                    <Link
                      key={item.title}
                      to={isDisabled ? '#' : item.url}
                      onClick={
                        isDisabled ? (e) => e.preventDefault() : undefined
                      }
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors relative',
                        isDisabled
                          ? 'text-sidebar-foreground/40 cursor-not-allowed'
                          : active
                            ? 'text-sidebar-foreground font-medium'
                            : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
                      )}
                    >
                      {active && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-primary" />
                      )}
                      <item.icon
                        className={cn(
                          'size-4 shrink-0',
                          active && 'text-primary'
                        )}
                      />
                      <span className="truncate">{item.title}</span>
                      {item.isPro && <ProBadge />}
                    </Link>
                  );
                })}
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  const renderFooter = () => (
    <SidebarFooter>
      <SidebarMenu>
        {isPaivSuperAdmin && (
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Org Configurator">
              <Link to="/admin-config">
                <Settings className="size-4" />
                <span>Org Configurator</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {!isStarter && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Switch to Classic View"
              onClick={() => navigate('/pipeline/board')}
            >
              <ArrowLeftRight className="size-4" />
              <span>Classic View</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
        <SidebarMenuItem>
          <SidebarMenuButton tooltip="Log out" onClick={handleLogout}>
            <LogOut className="size-4" />
            <span>Log out</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <div className="px-3 pb-2 pt-1 border-t border-border/40 group-data-[collapsible=icon]:hidden">
        <button
          className="w-full text-left cursor-pointer hover:bg-muted/50 rounded px-1 py-1 transition-colors"
          onClick={() => setOpenChangelog(true)}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              {orgData?.name && (
                <p className="text-[11px] font-medium text-muted-foreground truncate">
                  {orgData.name}
                </p>
              )}
              {(global as any)?.me?.email && (
                <p className="text-[11px] text-muted-foreground/70 truncate">
                  {(global as any).me.email}
                </p>
              )}
            </div>
            <span className="shrink-0 text-[10px] text-muted-foreground/60">
              {APP_VERSION}
            </span>
          </div>
        </button>
      </div>
      <Dialog open={openChangelog} onOpenChange={setOpenChangelog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Environment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Organization</span>
              <span className="font-medium text-foreground">
                {orgData?.name || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">User</span>
              <span className="font-medium text-foreground truncate ml-4 max-w-[200px]">
                {(global as any)?.me?.name || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium text-foreground truncate ml-4 max-w-[200px]">
                {(global as any)?.me?.email || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Version</span>
              <span className="font-medium text-foreground">{APP_VERSION}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SidebarFooter>
  );

  return (
    <Sidebar collapsible="icon" className="border-r-0" {...props}>
      <SidebarHeader className="pb-0">
        {renderLogo()}
        {renderUserProfile()}
      </SidebarHeader>
      <SidebarContent>
        {renderMainNav()}
        {!isStarter && renderPerformanceNav()}
        <SidebarSeparator className="mx-2" />
        {renderSettingsNav()}
      </SidebarContent>
      {renderFooter()}
    </Sidebar>
  );
}
